import { useCallback, useEffect, useRef, useState } from 'react';
import type { BgpRecentResponse } from '../lib/api.js';
import { fetchBgpRecent } from '../lib/api.js';
import {
  type DashboardResource,
  createDashboardResource,
  markResourceError,
  markResourceLoading,
  markResourceSuccess,
  updateResourceStaleness,
} from '../lib/dashboardResource.js';
import { useDashboard } from '../lib/store.js';

interface UseIncidentBgpFeedOptions {
  intervalMs?: number;
  staleAfterMs?: number;
}

interface UseIncidentBgpFeedResult {
  bgp: DashboardResource<BgpRecentResponse>;
  refreshBgp: () => Promise<void>;
}

export function useIncidentBgpFeed(
  incidentActive: boolean,
  { intervalMs = 5_000, staleAfterMs = 90_000 }: UseIncidentBgpFeedOptions = {},
): UseIncidentBgpFeedResult {
  const { setBgp } = useDashboard();
  const [bgp, setBgpResource] = useState(createDashboardResource<BgpRecentResponse>());
  const mountedRef = useRef(true);

  const refreshBgp = useCallback(async () => {
    if (!mountedRef.current) {
      return;
    }

    setBgpResource((resource) => markResourceLoading(resource));
    try {
      const payload = await fetchBgpRecent();
      if (!mountedRef.current) {
        return;
      }
      setBgp(payload);
      setBgpResource((resource) =>
        markResourceSuccess(resource, payload, new Date().toISOString()),
      );
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }
      setBgpResource((resource) => markResourceError(resource, error));
    }
  }, [setBgp]);

  useEffect(() => {
    mountedRef.current = true;

    if (!incidentActive) {
      setBgpResource(createDashboardResource<BgpRecentResponse>());
      return;
    }

    refreshBgp();
    const interval = setInterval(() => {
      refreshBgp();
    }, intervalMs);

    const freshness = setInterval(() => {
      setBgpResource((resource) => updateResourceStaleness(resource, staleAfterMs));
    }, 1_000);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      clearInterval(freshness);
    };
  }, [incidentActive, intervalMs, staleAfterMs, refreshBgp]);

  return { bgp, refreshBgp };
}
